/**
 * T1T Solutions — Sistema de Cotización
 * Agente: Quote System Developer
 *
 * Maneja el carrito de cotización de forma INDEPENDIENTE
 * al carrito nativo de Shopify, usando localStorage.
 *
 * Flujo:
 *   Producto → Agregar a Cotización → localStorage → /pages/mi-cotizacion → Formulario → Email ventas
 */

const T1TQuote = (() => {
  'use strict';

  /* ─── Constantes ─────────────────────────────────────── */
  const STORAGE_KEY  = 't1t_quote_items';
  const EVENT_UPDATE = 't1t:quote-updated';
  const QUOTE_URL    = '/pages/mi-cotizacion';

  /* ─── Persistencia en localStorage ───────────────────── */

  /**
   * Devuelve el array de items del quote.
   * @returns {Array}
   */
  function getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  /**
   * Guarda el array de items en localStorage y dispara el evento de actualización.
   * @param {Array} items
   */
  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent(EVENT_UPDATE, { detail: { items } }));
  }

  /**
   * Devuelve el número total de productos (suma de cantidades).
   * @returns {number}
   */
  function getCount() {
    return getItems().reduce((total, item) => total + item.qty, 0);
  }

  /* ─── Operaciones CRUD ────────────────────────────────── */

  /**
   * Agrega un producto al quote o incrementa su cantidad si ya existe.
   * @param {Object} product - { id, title, handle, image, vendor }
   * @param {number} qty - Cantidad a agregar (default 1)
   */
  function add(product, qty = 1) {
    const items    = getItems();
    const existing = items.find(i => i.id === product.id);

    if (existing) {
      existing.qty += qty;
    } else {
      items.push({
        id:     product.id,
        title:  product.title,
        handle: product.handle,
        image:  product.image || '',
        vendor: product.vendor || '',
        qty:    qty,
      });
    }

    saveItems(items);
    return items;
  }

  /**
   * Elimina un producto del quote por su ID.
   * @param {string|number} id
   */
  function remove(id) {
    const sid = String(id);
    saveItems(getItems().filter(i => String(i.id) !== sid));
  }

  /**
   * Actualiza la cantidad de un producto. Si qty <= 0, lo elimina.
   * @param {string|number} id
   * @param {number} qty
   */
  function updateQty(id, qty) {
    if (qty <= 0) return remove(id);
    const sid   = String(id);
    const items = getItems().map(i => String(i.id) === sid ? { ...i, qty } : i);
    saveItems(items);
  }

  /**
   * Vacía completamente el quote.
   */
  function clearAll() {
    saveItems([]);
  }

  /**
   * Devuelve true si un producto está en el quote.
   * @param {string|number} id
   * @returns {boolean}
   */
  function isInQuote(id) {
    const sid = String(id);
    return getItems().some(i => String(i.id) === sid);
  }

  /* ─── UI: Contador en navbar ──────────────────────────── */

  /**
   * Actualiza el badge del contador en el navbar.
   */
  function updateCounter() {
    const badges = document.querySelectorAll('[data-quote-counter]');
    const count  = getCount();

    badges.forEach(badge => {
      badge.textContent = count > 99 ? '99+' : count;
      // Mostrar/ocultar el contenedor padre (.cart-count-bubble de Dawn)
      // El span hijo tiene data-quote-counter; el div padre empieza con display:none
      const bubble = badge.closest('.cart-count-bubble') || badge;
      bubble.style.display = count > 0 ? 'flex' : 'none';
    });

    // Badge pill en botones "Ver Mi Cotización" (en hero del catálogo y otras páginas)
    document.querySelectorAll('[data-quote-counter-badge]').forEach(badge => {
      badge.textContent   = count > 99 ? '99+' : count;
      badge.style.display = count > 0 ? 'inline-flex' : 'none';
    });

    // También actualizar el aria-label del link del navbar
    const link = document.querySelector('[data-quote-nav-link]');
    if (link) {
      link.setAttribute('aria-label',
        count === 0
          ? 'Mi Cotización — sin productos'
          : `Mi Cotización — ${count} producto${count !== 1 ? 's' : ''}`
      );
    }
  }

  /* ─── UI: Botones en página de producto ──────────────── */

  /**
   * Inicializa todos los botones "Agregar a Cotización" de la página.
   */
  function initQuoteButtons() {
    document.querySelectorAll('[data-quote-btn]').forEach(btn => {
      const productId = btn.dataset.productId;
      if (!productId) return;

      // Estado inicial del botón
      refreshButtonState(btn, productId);

      btn.addEventListener('click', () => {
        const product = {
          id:     productId,
          title:  btn.dataset.productTitle || '',
          handle: btn.dataset.productHandle || '',
          image:  btn.dataset.productImage || '',
          vendor: btn.dataset.productVendor || '',
        };

        add(product);
        refreshButtonState(btn, productId);
        showFeedback(btn);
      });
    });
  }

  /**
   * Actualiza el texto/estado visual de un botón.
   * @param {Element} btn
   * @param {string|number} productId
   */
  function refreshButtonState(btn, productId) {
    const inQuote = isInQuote(productId);
    btn.classList.toggle('t1t-quote-btn--added', inQuote);

    const textEl = btn.querySelector('[data-btn-text]') || btn;
    if (inQuote) {
      textEl.textContent = '✓ En cotización';
    } else {
      textEl.textContent = 'Agregar a Cotización';
    }
  }

  /**
   * Muestra una animación de feedback al agregar.
   * @param {Element} btn
   */
  function showFeedback(btn) {
    btn.classList.add('t1t-quote-btn--pulse');
    setTimeout(() => btn.classList.remove('t1t-quote-btn--pulse'), 600);
  }

  /* ─── UI: Página de cotización ────────────────────────── */

  /**
   * Renderiza la lista de productos en la página /pages/mi-cotizacion.
   */
  function renderQuotePage() {
    const container = document.querySelector('[data-quote-items]');
    if (!container) return;

    const items    = getItems();
    const emptyMsg = document.querySelector('[data-quote-empty]');
    const formEl   = document.querySelector('[data-quote-form-wrapper]');

    if (items.length === 0) {
      container.innerHTML = '';
      if (emptyMsg)  emptyMsg.style.display  = 'block';
      if (formEl)    formEl.style.display     = 'none';
      return;
    }

    if (emptyMsg)  emptyMsg.style.display  = 'none';
    if (formEl)    formEl.style.display     = 'block';

    container.innerHTML = items.map(item => `
      <div class="t1t-quote-item" data-item-id="${item.id}">
        <div class="t1t-quote-item__image">
          ${item.image
            ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy">`
            : `<div class="t1t-quote-item__image-placeholder">
                 <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                   <rect x="3" y="3" width="18" height="18" rx="2"/>
                   <path d="M3 9h18M9 21V9"/>
                 </svg>
               </div>`
          }
        </div>
        <div class="t1t-quote-item__info">
          ${item.vendor ? `<p class="t1t-quote-item__vendor">${escapeHtml(item.vendor)}</p>` : ''}
          <p class="t1t-quote-item__title">
            <a href="/products/${escapeHtml(item.handle)}">${escapeHtml(item.title)}</a>
          </p>
        </div>
        <div class="t1t-quote-item__qty">
          <button class="t1t-qty-btn" data-action="decrease" data-id="${item.id}" aria-label="Reducir cantidad">−</button>
          <input
            type="number"
            class="t1t-qty-input"
            value="${item.qty}"
            min="1"
            max="999"
            data-id="${item.id}"
            aria-label="Cantidad de ${escapeHtml(item.title)}"
          >
          <button class="t1t-qty-btn" data-action="increase" data-id="${item.id}" aria-label="Aumentar cantidad">+</button>
        </div>
        <button class="t1t-remove-btn" data-id="${item.id}" aria-label="Eliminar ${escapeHtml(item.title)}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `).join('');

    // Actualizar campo oculto con la lista de productos para el formulario
    syncProductsToForm();
    // NOTA: los event listeners se vinculan UNA SOLA VEZ en initQuotePage()
    // No agregar addEventListener aquí para evitar duplicados en cada render
  }

  /**
   * Maneja clicks en la lista de productos (eliminar, +/-).
   */
  function handleListClick(e) {
    const btn = e.target.closest('[data-action], .t1t-remove-btn');
    if (!btn) return;

    const id = btn.dataset.id;

    if (btn.classList.contains('t1t-remove-btn')) {
      remove(id);
      renderQuotePage();
    } else if (btn.dataset.action === 'increase') {
      const item = getItems().find(i => i.id === id);
      if (item) updateQty(id, item.qty + 1);
      renderQuotePage();
    } else if (btn.dataset.action === 'decrease') {
      const item = getItems().find(i => i.id === id);
      if (item) updateQty(id, item.qty - 1);
      renderQuotePage();
    }
  }

  /**
   * Maneja cambios en el input de cantidad.
   */
  function handleListChange(e) {
    const input = e.target.closest('.t1t-qty-input');
    if (!input) return;

    const id  = input.dataset.id;
    const qty = parseInt(input.value, 10);
    if (!isNaN(qty)) {
      updateQty(id, qty);
      syncProductsToForm();
    }
  }

  /**
   * Sincroniza la lista de productos con el campo oculto del formulario.
   */
  function syncProductsToForm() {
    const productField = document.querySelector('[data-quote-products-field]');
    if (!productField) return;

    const items = getItems();
    const text  = items.map(i =>
      `• ${i.vendor ? i.vendor + ' ' : ''}${i.title} — Cant: ${i.qty}`
    ).join('\n');

    productField.value = text;
  }

  /* ─── Formulario de solicitud ─────────────────────────── */

  /**
   * Inicializa el formulario de solicitud de cotización.
   */
  function initQuoteForm() {
    const form = document.querySelector('[data-quote-form]');
    if (!form) return;

    form.addEventListener('submit', handleFormSubmit);
  }

  /**
   * Maneja el envío del formulario.
   * @param {Event} e
   */
  async function handleFormSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;

    if (!validateForm(form)) return;

    const submitBtn = form.querySelector('[data-submit-btn]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Enviando...';
    }

    // Sincronizar lista de productos antes de enviar
    syncProductsToForm();

    try {
      const formData = new FormData(form);

      // Construir el body del mensaje para Shopify contact form
      const nombre   = formData.get('contact[nombre]') || '';
      const empresa  = formData.get('contact[empresa]') || '';
      const telefono = formData.get('contact[telefono]') || '';
      const nit      = formData.get('contact[nit]') || '';
      const mensaje  = formData.get('contact[body]') || '';
      const productos = formData.get('contact[productos]') || '';

      const bodyText = [
        `📋 SOLICITUD DE COTIZACIÓN — T1T Solutions`,
        ``,
        `👤 Datos del contacto:`,
        `   Nombre:   ${nombre}`,
        `   Empresa:  ${empresa}`,
        `   Teléfono: ${telefono}`,
        `   NIT/CC:   ${nit}`,
        ``,
        `📦 Productos solicitados:`,
        productos,
        ``,
        `💬 Mensaje adicional:`,
        mensaje || '(sin mensaje adicional)',
      ].join('\n');

      // Enviar a Shopify contact form (email al administrador de la tienda)
      const shopifyForm = new FormData();
      shopifyForm.append('form_type', 'contact');
      shopifyForm.append('utf8', '✓');
      shopifyForm.append('contact[email]', formData.get('contact[email]') || '');
      shopifyForm.append('contact[name]', nombre);
      shopifyForm.append('contact[phone]', telefono);
      shopifyForm.append('contact[body]', bodyText);

      const response = await fetch('/contact', {
        method:  'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body:    shopifyForm,
      });

      if (response.ok) {
        showSuccessMessage(form);
        clearAll();
      } else {
        throw new Error('Error en el servidor');
      }
    } catch (err) {
      console.error('[T1TQuote] Error al enviar formulario:', err);
      showErrorMessage(form);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Solicitar Cotización';
      }
    }
  }

  /**
   * Valida los campos requeridos del formulario.
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function validateForm(form) {
    let valid = true;
    const required = form.querySelectorAll('[required]');

    required.forEach(field => {
      const errorEl = form.querySelector(`[data-error-for="${field.name}"]`);
      if (!field.value.trim()) {
        valid = false;
        field.classList.add('t1t-form__input--error');
        if (errorEl) errorEl.style.display = 'block';
      } else {
        field.classList.remove('t1t-form__input--error');
        if (errorEl) errorEl.style.display = 'none';
      }
    });

    // Validar email
    const emailField = form.querySelector('[type="email"]');
    if (emailField && emailField.value && !isValidEmail(emailField.value)) {
      valid = false;
      emailField.classList.add('t1t-form__input--error');
      const errorEl = form.querySelector(`[data-error-for="${emailField.name}"]`);
      if (errorEl) {
        errorEl.textContent = 'Ingresa un email válido';
        errorEl.style.display = 'block';
      }
    }

    if (!valid) {
      // Scroll al primer error
      const firstError = form.querySelector('.t1t-form__input--error');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return valid;
  }

  /**
   * Muestra el mensaje de confirmación de envío exitoso.
   * @param {HTMLFormElement} form
   */
  function showSuccessMessage(form) {
    const wrapper = document.querySelector('[data-quote-form-wrapper]');
    const success = document.querySelector('[data-quote-success]');
    const list    = document.querySelector('[data-quote-items]');

    if (wrapper) wrapper.style.display = 'none';
    if (list)    list.style.display    = 'none';
    if (success) {
      success.style.display = 'block';
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Muestra un mensaje de error en el formulario.
   * @param {HTMLFormElement} form
   */
  function showErrorMessage(form) {
    const errorBanner = form.querySelector('[data-form-error]');
    if (errorBanner) {
      errorBanner.style.display = 'block';
      errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* ─── Utilidades ──────────────────────────────────────── */

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /* ─── Inicialización global ───────────────────────────── */

  /**
   * Punto de entrada. Se llama cuando el DOM está listo.
   */
  function init() {
    // Escuchar actualizaciones del quote para refrescar el contador
    document.addEventListener(EVENT_UPDATE, updateCounter);

    // Inicializar contador inmediatamente
    updateCounter();

    // Inicializar botones de producto (si están en la página)
    initQuoteButtons();

    // Inicializar la página de cotización (si está activa)
    if (document.querySelector('[data-quote-page]')) {
      renderQuotePage();
      initQuoteForm();

      // Eventos delegados vinculados UNA SOLA VEZ sobre el contenedor persistente
      // (el innerHTML se reemplaza en cada render; el contenedor permanece)
      const listContainer = document.querySelector('[data-quote-items]');
      if (listContainer) {
        listContainer.addEventListener('click', handleListClick);
        listContainer.addEventListener('change', handleListChange);
      }
    }
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ─── API pública ─────────────────────────────────────── */
  return { add, remove, updateQty, getItems, getCount, clearAll, isInQuote };
})();
